import type { TFunction } from 'i18next';
import type { BookingsSite } from './calendar-sources';
import type { CalendarBookingCreate } from '../card-actions';
import { formatWhen, type CalendarFormatParts } from './calendar-format';
import type { CalendarItem } from './calendar-item';

// The Calendar's booking card: an appointment filled in step by step in the chat (the business, the client, the
// service, then the time and staff member), with no Maestro turn until the owner presses Book. Book makes the booking
// itself, through the Calendar's own checked booking write (PrepareCalendarAction `bookings.create`, then execute), so
// what was picked is what is booked; the harness then notes the booking in the card's chat, from its own reading and with
// no turn. The steps live only in this browser: a half-filled card is never a message, so it neither survives a reload
// nor reaches the model as an unanswered one.

export interface BookingService {
  id: string;
  name: string;
  minutes: number;
  price?: { value: string; currency: string };
  /** The site's staff who give it. Wix names the field for staff members but fills it with their resource ids. */
  staffMemberIds: string[];
}

/** An open time, epoch ms. */
export interface BookingSlot {
  start: number;
  end: number;
}

export interface BookingContact {
  id: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
}

export type BookingClient =
  | { kind: 'contact'; contact: BookingContact }
  | { kind: 'new'; firstName: string; lastName: string; email: string; phone: string };

export type BookingStep = 'site' | 'client' | 'service' | 'when';

export interface BookingDraft {
  /** Made once per card, so a repeated Book can never book the client twice. */
  requestId: string;
  siteId?: string;
  client?: BookingClient;
  service?: BookingService;
  /** Epoch ms. */
  start: number;
  staffResourceId?: string;
  notifyClient: boolean;
}

/** What opens a card: the stretch picked on the grid, and its staff member when picked in a staff member's column. */
export interface BookingCardStart {
  start: number;
  staffResourceId?: string;
  sites: BookingsSite[];
  /** The business clock the Calendar showed the pick in. */
  timeZone: string;
  /** A client already chosen, as when the owner adds another service for the client just booked. */
  client?: BookingClient;
}

/** A booking Book made: what the card picked, and the new booking's id, which its Undo cancels. */
export interface MadeBooking {
  draft: BookingDraft;
  bookingId: string;
  /** Set once Undo has cancelled it. */
  undone?: boolean;
  /** The messages already sent about it, which are not offered again. */
  sent?: BookedMessageKind[];
}

/** An open card, drawn at the end of the conversation the chat was on when it opened, and nowhere else. */
export interface OpenBookingCard {
  start: BookingCardStart;
  conversationId: string | null;
  requestId: string;
  /** For a card opened in a fresh chat, which one (`freshChat` in ActiveConversationContext); 0 otherwise. */
  freshChat: number;
  /** Set once Book has made the booking, kept with the card so a redraw keeps its confirmation. */
  booked?: MadeBooking;
}

/** Whether the open booking card belongs to the chat on screen: its conversation, or for a fresh one the same fresh chat. */
export function bookingCardIn(card: OpenBookingCard | null, conversationId: string | null, freshChat: number): OpenBookingCard | null {
  if (!card || card.conversationId !== conversationId) return null;
  return conversationId !== null || card.freshChat === freshChat ? card : null;
}

export function newBookingDraft(start: BookingCardStart, requestId: string): BookingDraft {
  const sites = bookableSites(start.sites);
  return {
    requestId,
    ...(sites.length === 1 ? { siteId: sites[0].siteId } : {}),
    ...(start.client ? { client: start.client } : {}),
    start: start.start,
    ...(start.staffResourceId ? { staffResourceId: start.staffResourceId } : {}),
    notifyClient: true,
  };
}

/** A site whose last calendar read failed stays: the card reads its services and contacts itself. */
export function bookableSites(sites: BookingsSite[]): BookingsSite[] {
  return sites.filter((site) => site.hasBookings);
}

/** The first step the draft still needs; a single business is never asked about. */
export function bookingStep(draft: BookingDraft): BookingStep {
  if (!draft.siteId) return 'site';
  if (!draft.client) return 'client';
  if (!draft.service) return 'service';
  return 'when';
}

/** The staff members who give the service, as the site's roster names them; the picked one first. */
export function serviceStaff(site: BookingsSite | undefined, service: BookingService | undefined, picked?: string) {
  if (!site || !service) return [];
  const staff = site.staff.filter((member) => service.staffMemberIds.includes(member.resourceId));
  return [...staff.filter((member) => member.resourceId === picked), ...staff.filter((member) => member.resourceId !== picked)];
}

/** The client as the card shows them: their name, else the email or phone a contact without one is known by. */
export function clientName(client: BookingClient): string {
  const [first, last] = client.kind === 'contact' ? [client.contact.firstName, client.contact.lastName] : [client.firstName, client.lastName];
  const name = [first, last].filter(Boolean).join(' ').trim();
  if (name || client.kind !== 'contact') return name;
  return (client.contact.email || client.contact.phone || '').trim();
}

/** A new client needs a first name and a way to reach them, as Wix needs to add them to the contacts. */
export function newClientReady(client: Extract<BookingClient, { kind: 'new' }>): boolean {
  return !!client.firstName.trim() && (!!client.email.trim() || !!client.phone.trim());
}

export function bookReady(draft: BookingDraft): boolean {
  return !!(draft.siteId && draft.client && draft.service && draft.staffResourceId && Number.isFinite(draft.start));
}

/** The new appointment Book prepares; undefined until every step is filled. */
export function bookCreate(draft: BookingDraft): CalendarBookingCreate | undefined {
  if (!bookReady(draft) || !draft.client || !draft.service || !draft.staffResourceId) return undefined;
  const client = draft.client;
  const given = (key: keyof CalendarBookingCreate, value: string) => (value.trim() ? { [key]: value.trim() } : {});
  return {
    serviceId: draft.service.id,
    start: new Date(draft.start).toISOString(),
    staffResourceId: draft.staffResourceId,
    ...(client.kind === 'contact'
      ? { contactId: client.contact.id }
      : {
          ...given('clientFirstName', client.firstName),
          ...given('clientLastName', client.lastName),
          ...given('clientEmail', client.email),
          ...given('clientPhone', client.phone),
        }),
  };
}

/** Book's label: with the service's fixed price when it has one. */
export function bookTitle(service: BookingService | undefined, t: TFunction, parts: CalendarFormatParts): string {
  return service?.price ? t('calendar.book.bookWithPrice', { price: servicePrice(service, parts) }) : t('calendar.book.book');
}

export function servicePrice(service: BookingService, parts: CalendarFormatParts): string {
  return service.price ? parts.price(service.price.value, service.price.currency) : '';
}

/** The staff member's name as the booking's site lists them; empty when the roster does not. */
export function bookedStaffName(sites: BookingsSite[], made: MadeBooking): string {
  return sites.find((site) => site.siteId === made.draft.siteId)?.staff.find((member) => member.resourceId === made.draft.staffResourceId)?.name ?? '';
}

const bookedEnd = (draft: BookingDraft) => draft.start + (draft.service?.minutes ?? 0) * 60_000;

/** "Booked · {service} with {client} · {when} · {staff} · {price}", leaving out what the booking does not have. */
export function bookedLine(made: MadeBooking, staff: string, t: TFunction, parts: CalendarFormatParts): string {
  const { draft } = made;
  const client = draft.client ? clientName(draft.client) : '';
  const service = draft.service;
  return [
    t('calendar.book.booked'),
    service && client ? t('calendar.book.serviceWithClient', { service: service.name, client }) : (service?.name ?? client),
    service ? formatWhen(parts, draft.start, bookedEnd(draft)) : '',
    staff,
    service ? servicePrice(service, parts) : '',
  ].filter(Boolean).join(' · ');
}

/** Said only when Book told the client; how they were told depends on what Wix holds for them, so it names no way. */
export function notifiedLine(made: MadeBooking, t: TFunction): string | undefined {
  const client = made.draft.client ? clientName(made.draft.client) : '';
  return made.draft.notifyClient && client ? t('calendar.book.notified', { client }) : undefined;
}

/**
 * The booking Book made, as the Calendar reads one, so its Undo runs the details card's own cancel (the harness reads
 * the live booking and the owner confirms it). It holds no client name: the name is anyone's to write through the
 * site's forms.
 */
export function bookedItem(made: MadeBooking, staff: string, siteName?: string): CalendarItem | undefined {
  const { draft, bookingId } = made;
  if (!draft.service || !draft.siteId || !bookingId) return undefined;
  return {
    key: `booking:${bookingId}`,
    kind: 'booking',
    title: draft.service.name,
    start: draft.start,
    end: bookedEnd(draft),
    allDay: false,
    sourceId: draft.siteId,
    ...(siteName ? { sourceLabel: siteName } : {}),
    booking: { clients: [{ id: bookingId, status: 'confirmed' }] },
    ...(staff ? { staff: [staff] } : {}),
    ...(draft.staffResourceId ? { staffResourceIds: [draft.staffResourceId] } : {}),
    ...(draft.service.price ? { price: { amount: draft.service.price.value, currency: draft.service.price.currency } } : {}),
    ref: { siteId: draft.siteId, type: 'APPOINTMENT', bookingId, serviceId: draft.service.id },
  };
}

/** The owner's messages about the booking just made. */
export type BookedMessageKind = 'askPayment' | 'repeatWeekly';

/**
 * What Ask for payment and Repeat weekly send Maestro: plain owner text that names the booking by its id, the one key
 * that tells it from a rebooking of the same slot in the same chat (the service, time and staff member follow only to be
 * readable), never by the client's name. Nothing rides the message: a USER message carrying an item card is drawn as that
 * card instead of its words, and the harness keeps the card from the model. Ask for payment only for a service with a
 * fixed price; a message already sent is not offered again.
 */
export function bookedMessages(
  made: MadeBooking,
  staff: string,
  t: TFunction,
  parts: CalendarFormatParts,
): Partial<Record<BookedMessageKind, string>> {
  const service = made.draft.service;
  if (!service || !made.bookingId || made.undone) return {};
  const values = {
    bookingId: made.bookingId,
    service: service.name,
    when: formatWhen(parts, made.draft.start, bookedEnd(made.draft)),
    staff,
  };
  const sent = made.sent ?? [];
  return {
    ...(service.price && !sent.includes('askPayment') ? { askPayment: t('calendar.book.askPaymentMessage', values) } : {}),
    ...(!sent.includes('repeatWeekly') ? { repeatWeekly: t('calendar.book.repeatWeeklyMessage', values) } : {}),
  };
}

/** The chat on screen, as far as whether it can take a message now. */
export interface BookedCardChat {
  conversationId: string | null;
  /** A reply running or awaited. */
  turnInFlight: boolean;
  /** WhatsApp or SMS owns the chat, so the web sends nothing into it. */
  externallyOwned: boolean;
  /** A handed-off message is waiting to be sent in it. */
  prefillWaiting: boolean;
}

/**
 * Whether the booked card's message chips can send now: in the card's own chat, the one the booking is noted in (so
 * Maestro knows which booking the words mean), and only while that chat could take a message typed now. A chip that
 * cannot send is disabled rather than parked, so a message is never left waiting or lost.
 */
export function bookedCardCanAsk(card: Pick<OpenBookingCard, 'conversationId'>, chat: BookedCardChat): boolean {
  return !!card.conversationId && chat.conversationId === card.conversationId && !chat.turnInFlight && !chat.externallyOwned && !chat.prefillWaiting;
}

/**
 * The client the next card books: an existing contact as picked; a new one by the contact the booking made of them,
 * read back from the one-booking read (the client on this booking), since their details sent again would add them to
 * the contacts a second time; with no contact read, none, and that card asks who it is for.
 */
export function nextClient(made: MadeBooking, read?: CalendarItem): BookingClient | undefined {
  const client = made.draft.client;
  if (!client || client.kind === 'contact') return client;
  const contactId = read?.booking?.clients.find((each) => each.id === made.bookingId)?.contactId;
  if (!contactId) return undefined;
  const given = (key: keyof BookingContact, value: string) => (value.trim() ? { [key]: value.trim() } : {});
  return {
    kind: 'contact',
    contact: { id: contactId, ...given('firstName', client.firstName), ...given('lastName', client.lastName), ...given('email', client.email), ...given('phone', client.phone) },
  };
}

/** Where Add another service starts: the same business, at the end of the booking just made, with the same staff member first. */
export function nextServiceStart(card: OpenBookingCard, made: MadeBooking, read?: CalendarItem): BookingCardStart {
  const { draft } = made;
  const client = nextClient(made, read);
  return {
    start: bookedEnd(draft),
    ...(draft.staffResourceId ? { staffResourceId: draft.staffResourceId } : {}),
    sites: card.start.sites.filter((site) => site.siteId === draft.siteId),
    timeZone: card.start.timeZone,
    ...(client ? { client } : {}),
  };
}

/**
 * What the card says when Book did not book: a taken slot names the fix, and an unknown outcome says to look first. A
 * repeat of a Book the harness holds as just done is the same unknown, since the first press may have made the booking.
 */
export function bookRefusalKey(reason: string): 'calendar.book.notAvailable' | 'calendar.book.unknown' | 'common.error.generic' {
  if (reason === 'NOT_AVAILABLE') return 'calendar.book.notAvailable';
  if (reason === 'OUTCOME_UNKNOWN' || reason === 'RECENTLY_DONE') return 'calendar.book.unknown';
  return 'common.error.generic';
}
